import React, {useEffect} from "react";
import Header from "components/Header";
import Survey from "components/Survey";
import {useFeatureLane, useSurvey} from "data/access";
import {SaveSurveyQuestionValue, UserSurveyDatabaseSetterValue} from "data/access/surveys";


import FeatureLaneBreadCrumb from "components/Survey/FeatureLaneBreadCrumb";
import Loading from "components/Loading";
import {useNavigate, useParams} from "react-router-dom";
import {FeatureKeyForNavigation, surveyQuestionPath, SurveyQuestionPath} from "hooks/page-navigation/survey";
import {QuestionKey, SurveyKey} from "data/models/survey/keys";
import {assertNoError, assertNotNullOrUndefined} from "helpers/asserts";
import {
  DeliverableGroupKey,
  FeatureKey,
  FeatureLaneDefinitionModel,
  FeatureLaneModelWithStatus, UserSurveyStatusModelForFeatureLane
} from "data/models/feature-lane";
import {UserSurveyRenderableModel} from "data/models/survey";
import {isNullOrUndefined} from "helpers/is-null-or-undefined";
import {getAuth} from "firebase/auth";
import {useAuthState} from "react-firebase-hooks/auth";

type SurveyPageProps = {
  deliverableGroupIdProp?: DeliverableGroupKey;
  featureIdProp?: FeatureKey;
  surveyIdProp?: SurveyKey;
}


const SurveyPage = (
    {
      deliverableGroupIdProp,
      featureIdProp,
      surveyIdProp,
    }: SurveyPageProps
) => {
  const navigate = useNavigate();
  const {
    deliverableGroupId,
    featureIdForNavigation,
    surveyIdForNavigation,
    questionIdForNavigation
  } = useParams<SurveyQuestionPath>();
  const deliverableGroupIdToUse = deliverableGroupIdProp ?? deliverableGroupId;
  const featureIdToUse = featureIdProp ?? featureIdForNavigation;
  const surveyIdToUse = surveyIdProp ?? surveyIdForNavigation;
  const [survey, featureDefinition, loading, surveyError, saveSurveyQuestionValue] = useSurvey(
      deliverableGroupIdToUse,
      featureIdToUse,
      surveyIdToUse
  );

  const [
    featureLanes,
    featureLanesLoading,
    featureLanesError
  ] = useFeatureLane(deliverableGroupIdToUse);

  const featureLane = featureLanes?.find(fl => fl.id === featureDefinition?.id);
  const currentSurvey = featureLane?.userSurveys.find(s => s.id === survey?.id);

  const auth = getAuth();
  const [user,,] = useAuthState(auth);


  useEffect(() => {
    if (isNullOrUndefined(user)) {
      return;
    }
    const navigateToSurvey = localStorage.getItem("navigateToSurvey");
    if (isNullOrUndefined(navigateToSurvey)) {
      return;
    }
    localStorage.removeItem("navigateToSurvey");
    navigate(navigateToSurvey);
  }, [user]);

  useEffect(() => {
    if (
        currentSurvey === undefined
        || canNavigateTo(currentSurvey)
    ) {
      return;
    }
    const timeoutId = setTimeout(() => {
      navigate("/");
    }, 3333);
    return (() => clearTimeout(timeoutId));
  }, [currentSurvey]);

  if (
      currentSurvey !== undefined
      && !canNavigateTo(currentSurvey)
  ) {
    return <>
      <Header saveFormCallback={null} />
      <div className={"mt-32 text-center"}>
        {currentSurvey.statusReason}.
      </div>
      <div className={"mt-12 text-center"}>
        Navigating to dashboard ...
      </div>
    </>;
  }

  if (loading || featureLanesLoading) {
    return <>
      <Header saveFormCallback={null} />
      <Loading className={"mt-32"} loadingText={"Loading Survey..."}/>
    </>;
  }

  assertNoError(
      surveyError
      ?? featureLanesError
  );

  if (
      survey === null
      || featureDefinition === null
      || saveSurveyQuestionValue === undefined
      || featureLanes === null
      || featureLane === undefined
      || questionIdForNavigation === undefined
      || deliverableGroupIdToUse === undefined
  ) {
    return <div>An error happened. Try again later.</div>
  }

  const currentSurveyIndex = featureLane.userSurveys.map(survey => survey.id).indexOf(survey.id);
  if (currentSurveyIndex < 0) {
    throw new Error(`navigateToSurvey: Invalid surveyId=${surveyIdToUse}`);
  }

  function handleChange(questionId: QuestionKey, value: UserSurveyDatabaseSetterValue): Promise<void> {
    assertNotNullOrUndefined<SaveSurveyQuestionValue>(saveSurveyQuestionValue);
    assertNotNullOrUndefined<FeatureLaneDefinitionModel>(featureDefinition);
    assertNotNullOrUndefined<UserSurveyRenderableModel>(survey);
    return saveSurveyQuestionValue(featureDefinition.id, survey.id, questionId, value);
  }

  function canNavigateTo(toSurvey: UserSurveyStatusModelForFeatureLane) {
    // "company-description" is an exception to open the page while the user is logged in and the queued data is stored to the DB.
    return toSurvey.id === "business-info" || toSurvey.status !== "blocked" || !(toSurvey.blockedBy?.some(s => s !== survey?.id) ?? false);
  }

  function getPreviousSurveyButtonTitle() {
    assertNotNullOrUndefined<FeatureLaneModelWithStatus>(featureLane);
    if (currentSurveyIndex > 0) {
      if (canNavigateTo(featureLane.userSurveys[currentSurveyIndex - 1])) {
        return "Back";
      }
    } else {
      const previousFeatureLane = getPreviousFeatureLane();
      if (previousFeatureLane !== null && canNavigateTo(previousFeatureLane.userSurveys[0])) {
        return `Back to ${previousFeatureLane.title}`;
      }
    }
    return "Back to Dashboard";
  }

  function getNextSurveyButtonTitle() {
    assertNotNullOrUndefined<FeatureLaneModelWithStatus>(featureLane);
    if (currentSurveyIndex < featureLane.userSurveys.length - 1) {
      if (canNavigateTo(featureLane.userSurveys[currentSurveyIndex + 1])) {
        return "Next";
      }
    } else {
      const nextFeatureLane = getNextFeatureLane();
      if (nextFeatureLane !== null && canNavigateTo(nextFeatureLane.userSurveys[0])) {
        return `Continue to ${nextFeatureLane.title}`;
      }
    }
    return currentSurvey?.id === "words-and-messages" ? "Get Your Brand Styles!" : "Back to Dashboard";
  }

  function navigateToPreviousUserSurvey() {
    assertNotNullOrUndefined<DeliverableGroupKey>(deliverableGroupIdToUse);
    assertNotNullOrUndefined<FeatureKeyForNavigation>(featureIdToUse);
    assertNotNullOrUndefined<FeatureLaneModelWithStatus>(featureLane);

    if (currentSurveyIndex > 0) {
      if (canNavigateTo(featureLane.userSurveys[currentSurveyIndex - 1])) {
        navigate(surveyQuestionPath(
            deliverableGroupIdToUse,
            featureIdToUse,
            featureLane.userSurveys[currentSurveyIndex - 1].id,
            "last"
        ));
        return;
      }
    } else {
      const previousFeatureLane = getPreviousFeatureLane();
      if (
          previousFeatureLane !== null
          && canNavigateTo(
              previousFeatureLane.userSurveys[previousFeatureLane.userSurveys.length - 1]
          )) {
        navigate(surveyQuestionPath(
            deliverableGroupIdToUse,
            previousFeatureLane.id,
            "last",
            "last"
        ))
      }
    }
    navigate("/");  // to dashboard
  }

  function navigateToNextUserSurvey() {
    assertNotNullOrUndefined<DeliverableGroupKey>(deliverableGroupIdToUse);
    assertNotNullOrUndefined<FeatureKeyForNavigation>(featureIdToUse);
    assertNotNullOrUndefined<FeatureLaneModelWithStatus>(featureLane);

    if (currentSurveyIndex < featureLane.userSurveys.length - 1) {
      if (canNavigateTo(featureLane.userSurveys[currentSurveyIndex + 1])) {
        navigate(surveyQuestionPath(
            deliverableGroupIdToUse,
            featureIdToUse,
            featureLane.userSurveys[currentSurveyIndex + 1].id,
            "first"
        ));
        return;
      }
    } else {
      const nextFeatureLane = getNextFeatureLane();
      if (nextFeatureLane !== null && canNavigateTo(nextFeatureLane.userSurveys[0])) {
        navigate(surveyQuestionPath(
            deliverableGroupIdToUse,
            nextFeatureLane.id,
            "first",
            "first"
        ));
        return;
      }
    }
    navigate("/");  // to dashboard
  }

  function getNextFeatureLane(): FeatureLaneModelWithStatus | null {
    assertNotNullOrUndefined<FeatureLaneModelWithStatus[]>(featureLanes);
    const featureLaneIndex = getFeatureLaneIndex();
    if (featureLaneIndex < featureLanes.length - 1) {
      return featureLanes[featureLaneIndex + 1];
    }
    return null;
  }

  function getPreviousFeatureLane(): FeatureLaneModelWithStatus | null {
    assertNotNullOrUndefined<FeatureLaneModelWithStatus[]>(featureLanes);
    const featureLaneIndex = getFeatureLaneIndex();
    if (featureLaneIndex > 0) {
      return featureLanes[featureLaneIndex - 1];
    }
    return null;
  }

  function getFeatureLaneIndex() {
    assertNotNullOrUndefined<FeatureLaneModelWithStatus[]>(featureLanes);
    assertNotNullOrUndefined<FeatureLaneDefinitionModel>(featureDefinition);
    const featureLaneIndex = featureLanes.map(fl => fl.id).indexOf(featureDefinition.id);
    if (featureLaneIndex < 0) {
      throw new Error(`Unexpected condition: ${featureDefinition.id} not found in FeatureLanes.`);
    }
    return featureLaneIndex;
  }

  function saveFormCallbackWrapper(): [
    (() => Promise<boolean>),
    (saveFormCallbackFnToUse: (() => Promise<boolean>)) => void,
  ] {
    let saveFormCallbackFn: (() => Promise<boolean>) | null = null;

    function setter(saveFormCallbackFnToUse: (() => Promise<boolean>)) {
      saveFormCallbackFn = saveFormCallbackFnToUse;
    }

    function caller(): Promise<boolean> {
      if (saveFormCallbackFn != null) {
        return saveFormCallbackFn();
      }
      return Promise.resolve(true);
    }

    return [caller, setter];
  }

  const [saveFormCallback, setSaveFormCallback] = saveFormCallbackWrapper();

  return (<>
    <Header saveFormCallback={saveFormCallback} />
    <div className={"flex flex-col mx-auto px-4 md:px-0"}>
      {deliverableGroupId !== "deliverables" && <div className={"mt-1 mx-auto w-3/4"}>
        <FeatureLaneBreadCrumb
            deliverableGroupId={deliverableGroupIdToUse}
            featureLane={featureLane}
            currentFeatureSurveyId={survey.id}
        />
      </div>}
      <Survey
          survey={survey}
          questionIdForNavigation={questionIdForNavigation}
          previousSurveyButtonTitle={getPreviousSurveyButtonTitle()}
          navigateToPreviousUserSurvey={navigateToPreviousUserSurvey}
          nextSurveyButtonTitle={getNextSurveyButtonTitle()}
          navigateToNextUserSurvey={navigateToNextUserSurvey}
          onChange={handleChange}
          setSaveFormCallback={setSaveFormCallback}
      />
    </div>
  </>);
}

export default SurveyPage;
